import { all, call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';
import { change, getFormValues, isValid } from 'redux-form';
import { push } from 'redux-first-history';

import { hideLoader, showLoader } from '@topconagriculture/loader';
import { FormName } from '@topconagriculture/feed-types';

import {
  createNewDelivery,
  deleteOrder,
  editOrder,
  getIngredientCurrentPriceDate,
  setDeliveryPreview,
} from './ingredientsInOrder.actions';
import { Forms } from '../../form/form.selector';
import api from '../../../core/api';
import { LoadersNames } from '../../ui/ui.constants';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { composeErrorMessage, transformFormValueToNumber } from '../../../core/helpers/utils/utils';
import { inventoryPaths } from '../../../routes/Inventory/inventoryPaths';
import { getSelectedIngredientInOrder } from './ingredientsInOrder.selector';
import { getSelectedOrderId } from '../../selected/selected.selector';
import { CreationDeliveryPayloadItem, CreationOrderPayloadItem } from '../../entities/orders/orders.types';
import { successHandler } from '../../../core/helpers/utils/sagaUtils';
import { setSelectedOrderId } from '../../selected/selected.actions';
import { CodeTypeNames } from '../../entities/code/code.types';
import { getCodeListFilteredByTypeNormalized } from '../../entities/code/code.selector';
import { getAllIngredients } from '../../entities/ingredients/ingredients.selector';
import { toFarmTimeZone, toTimezoneDate, toUTCDate } from '../../../core/helpers/utils/dateUtils';
import { settingsSelector } from '../../selectors';
import { getIngredientPrices } from '../../entities/dataTools/dataTools.actions';

const createFormattedDateString = (date: Date, withTime: boolean) => {
  const minutes = date.getMinutes();
  const hours = date.getHours();
  const day = date.getDate();
  const month = date.getMonth() + 1;
  const year = date.getFullYear();
  const formattedMinutes = (minutes > 9 ? '' : '0') + minutes;
  const formattedHours = (hours > 9 ? '' : '0') + hours;
  const formattedMonth = (month > 9 ? '' : '0') + month;
  const formattedDay = (day > 9 ? '' : '0') + day;
  return `${year}-${formattedMonth}-${formattedDay}` + (withTime ? `T${formattedHours}:${formattedMinutes}` : '');
};

function* createOrderIngredientsPayload() {
  const { supplierId, referenceNo, invoiceNo, weighbridgeTicketIds, ...restFields } = yield select(
    getFormValues(Forms.orderCreationForm),
  );
  const ingredientsInOrder = yield select(getSelectedIngredientInOrder);

  const reducer = (prev, curr) => {
    const [key, value] = curr;
    const [fieldName, uiId, ingredientId] = key.split('_');
    const notes = ingredientsInOrder.find((ingredient) => ingredient.uiId === uiId)?.notes;
    const id = ingredientsInOrder.find((ingredient) => ingredient.uiId === uiId)?.id;
    const statusCode = ingredientsInOrder.find((ingredient) => ingredient.uiId === uiId)?.statusCode;

    if (prev[uiId]) {
      return { ...prev, [uiId]: { ...prev[uiId], [fieldName]: value } };
    }

    return { ...prev, [uiId]: { [fieldName]: value, ingredientId, statusCode: statusCode || 1, notes, id } };
  };

  return Object.entries(restFields)
    .filter(([_key, value]) => value !== null)
    .reduce(reducer, {});
}

function* editOrderSaga({ payload }) {
  yield put(showLoader(LoadersNames.createOrder));
  const { isEdit } = payload;
  try {
    const { supplierId, referenceNo, invoiceNo } = yield select(getFormValues(Forms.orderCreationForm));

    const allAvailableIngredients = yield select(getAllIngredients);
    const priceUnitCodes = (yield select(getCodeListFilteredByTypeNormalized))({
      codeType: CodeTypeNames.priceUnitCodes,
      includeInactive: true,
    });
    const ingredients = yield call(createOrderIngredientsPayload);
    const id = yield select(getSelectedOrderId);
    let orderId;

    const items = Object.values(ingredients).map((item: CreationOrderPayloadItem) => {
      const priceUnitMultiplier: number =
        priceUnitCodes[allAvailableIngredients.find(({ id }) => id === item.ingredientId).priceUnitCode].value1;

      const formattedDate = createFormattedDateString(item[`plannedDate`] as Date, false);

      return {
        ...item,
        plannedDate: formattedDate,
        plannedWeight: transformFormValueToNumber(item[`plannedWeight`]) * priceUnitMultiplier,
        plannedPrice: transformFormValueToNumber(item[`plannedPrice`]) / priceUnitMultiplier,
      };
    }) as CreationOrderPayloadItem[];
    const requestPayload = { supplierId, referenceNo, invoiceNo, typeCode: 1, items };

    if (isEdit) {
      const result = yield call(api.orders.updateOrder, requestPayload, FormName.orderEdit, id);
      orderId = result.id;
    } else {
      const result = yield call(api.orders.createOrder, requestPayload, FormName.orderEdit);
      orderId = result.id;
    }
    yield put(setSelectedOrderId(orderId));
    yield put(notificationSuccess(isEdit ? 'inventory:edit_order_success' : 'inventory:add_order_success'));
    yield put(push(inventoryPaths.orderIngredients));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  } finally {
    yield put(hideLoader(LoadersNames.createOrder));
  }
}

function* createNewDeliverySaga(action: ReturnType<typeof createNewDelivery.started>) {
  yield put(showLoader(LoadersNames.createOrder));
  const { noSave, newPriceDateIngredients } = action.payload;
  try {
    const formIsValid = yield select(isValid(Forms.orderCreationForm));

    if (noSave && !formIsValid) {
      return;
    }

    const ingredientsInOrder = yield select(getSelectedIngredientInOrder);
    const timezone = yield select(settingsSelector.getTimeZone);
    const { supplierId, referenceNo, invoiceNo, weighbridgeTicketIds } = yield select(
      getFormValues(Forms.orderCreationForm),
    );
    const allAvailableIngredients = yield select(getAllIngredients);
    const priceUnitCodes = (yield select(getCodeListFilteredByTypeNormalized))({
      codeType: CodeTypeNames.priceUnitCodes,
      includeInactive: true,
    });
    const ingredients = yield call(createOrderIngredientsPayload);

    const items = Object.entries(ingredients).map(([uuId, item]: [string, CreationDeliveryPayloadItem]) => {
      const priceUnitMultiplier: number =
        priceUnitCodes[allAvailableIngredients.find(({ id }) => id === item.ingredientId).priceUnitCode].value1;

      // get formatted actual price date in case of new delivery
      let date = toTimezoneDate(new Date(item.actualPriceDate), timezone);
      const actualPriceDateFormatted = createFormattedDateString(date, true);

      // convert date to UTC only in case of new delivery
      date = toTimezoneDate(new Date(item[`actualDate`]), timezone);
      const formattedDate = createFormattedDateString(date, true);

      let actualPriceDate;

      if (noSave) {
        actualPriceDate = newPriceDateIngredients?.has(uuId) ? actualPriceDateFormatted : undefined;
      } else {
        actualPriceDate = actualPriceDateFormatted;
      }

      return {
        ...item,
        weighbridgeTicketIds,
        invoiceNo,
        statusCode: 2,
        actualDate: formattedDate,
        actualPriceDate: actualPriceDate,
        actualDryMatter: transformFormValueToNumber(item.actualDryMatter),
        // In case of processing weighbridge ticket the weight shouldn't be multiplied with price unit multiplier
        actualWeight: transformFormValueToNumber(item.actualWeight) * (weighbridgeTicketIds ? 1 : priceUnitMultiplier),
        actualPrice: transformFormValueToNumber(item.actualPrice) / priceUnitMultiplier,
      };
    }) as CreationDeliveryPayloadItem[];
    const requestPayload = { supplierId, referenceNo, typeCode: 2, items };

    const result = yield call(api.orders.createOrder, requestPayload, FormName.orderEdit, noSave);
    if (noSave) {
      for (const [index, ingredient] of ingredientsInOrder.entries()) {
        const { uiId, ingredientId } = ingredient;
        const receivedPriceDate = result.items[index]?.receiveData?.priceDate;
        const priceDate = toFarmTimeZone(receivedPriceDate ? new Date(receivedPriceDate) : new Date(), timezone);
        yield put(change(Forms.orderCreationForm, `actualPriceDate_${uiId}_${ingredientId}`, priceDate));
      }
      yield put(setDeliveryPreview(result));
    } else {
      yield put(notificationSuccess('inventory:add_order_success'));
      successHandler(action.meta);
    }
  } catch (err) {
    if (!noSave) {
      yield put(notificationError(composeErrorMessage(err)));
    }
  } finally {
    yield put(hideLoader(LoadersNames.createOrder));
  }
}

function* getIngredientCurrentPriceDateSaga(action: ReturnType<typeof getIngredientCurrentPriceDate.started>) {
  yield put(showLoader(LoadersNames.createOrder));
  const { ingredientId, actualPriceDateField } = action.payload;
  const timezone = yield select(settingsSelector.getTimeZone);
  try {
    yield put(getIngredientPrices.started(ingredientId));
    const {
      payload: { result },
    } = yield take(getIngredientPrices.done);
    const priceDate = result?.find((date) => new Date(date.timeStamp) < new Date());
    yield put(
      change(
        Forms.orderCreationForm,
        actualPriceDateField,
        toFarmTimeZone(priceDate ? new Date(priceDate.timeStamp) : toUTCDate(new Date()), timezone),
      ),
    );
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  } finally {
    yield put(hideLoader(LoadersNames.createOrder));
  }
}

function* deleteOrderSaga(action: ReturnType<typeof deleteOrder.started>) {
  try {
    const { payload, meta } = action;
    yield call(api.orders.deleteOrder, payload.orderId);
    successHandler(meta);
    yield put(notificationSuccess('inventory:delete_order_success'));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  } finally {
  }
}

export function* watchAppOrdersSaga() {
  yield all([
    yield takeLatest(editOrder.started, editOrderSaga),
    yield takeLatest(deleteOrder.started, deleteOrderSaga),
    yield takeLatest(createNewDelivery.started, createNewDeliverySaga),
    yield takeEvery(getIngredientCurrentPriceDate.started, getIngredientCurrentPriceDateSaga),
  ]);
}
